const MAX_IMAGE_SIZE = 2 * 1000 * 1000
const TARGET_SIZE = MAX_IMAGE_SIZE - 1024
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png"]
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"]
export const IMAGE_FILE_ACCEPT = ".jpg,.jpeg,.png,image/jpeg,image/jpg,image/png"

const START_QUALITY = 0.85
const MIN_QUALITY = 0.5
const FALLBACK_MIN_QUALITY = 0.05
const MAX_DIMENSION = 2560      // cap long edge up front
const MIN_SCALE = 0.25
const MAX_ATTEMPTS = 5

// ---------- Validation ----------

export function getImageExtension(file) {
    if (file instanceof File) {
        return file.name.split(".").pop().toLowerCase()
    }

    if (typeof file !== "string") {
        return ""
    }

    const fileName = file.split(/[?#]/)[0].split(/[\\/]/).pop()
    const extension = fileName?.split(".").pop()
    return extension && extension !== fileName ? extension.toLowerCase() : ""
}

export function validateImageFile(file, options = {}) {
    const allowCompression = options.allowCompression || false

    if (!(file instanceof File)) {
        return "Please select an image."
    }

    if (file.size > MAX_IMAGE_SIZE && !allowCompression) {
        return "Image size must be 2 MB or less."
    }

    const extension = getImageExtension(file)
    const hasValidExtension = ALLOWED_EXTENSIONS.includes(extension)
    const hasValidType = ALLOWED_TYPES.includes(file.type)

    if (!hasValidExtension || !hasValidType) {
        return "Only JPG, JPEG, and PNG images are allowed."
    }

    return "" // empty string means no error
}

// ---------- Helpers ----------

// createImageBitmap decodes off the main thread and is much faster than <img>
async function loadImage(file) {
    if (typeof createImageBitmap === "function") {
        try {
            return await createImageBitmap(file)
        } catch {
            // fall through to <img> decoding
        }
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
            URL.revokeObjectURL(url)
            // normalize to the same shape as ImageBitmap
            image.width = image.naturalWidth
            image.height = image.naturalHeight
            resolve(image)
        }
        image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("Unable to read image."))
        }
        image.src = url
    })
}

function drawOnCanvas(image, scale) {
    const canvas = document.createElement("canvas")
    canvas.width = Math.max(1, Math.round(image.width * scale))
    canvas.height = Math.max(1, Math.round(image.height * scale))

    const context = canvas.getContext("2d")
    if (!context) {
        throw new Error("Unable to prepare image for compression.")
    }

    context.imageSmoothingQuality = "high"
    context.fillStyle = "#fff" // JPEG has no alpha
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return canvas
}

function canvasToBlob(canvas, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            blob ? resolve(blob) : reject(new Error("Unable to compress image."))
        }, "image/jpeg", quality)
    })
}

// Returns { blob, fits }. Uses as few encodes as possible.
async function encodeWithin(canvas, minimumQuality) {
    // 1) Try a good quality first. Most images fit right here (1 encode).
    const first = await canvasToBlob(canvas, START_QUALITY)
    if (first.size < TARGET_SIZE) {
        return { blob: first, fits: true }
    }

    // 2) Check the floor. If it doesn't fit, tell the caller to shrink (1 encode).
    const lowest = await canvasToBlob(canvas, minimumQuality)
    if (lowest.size >= TARGET_SIZE) {
        return { blob: lowest, fits: false }
    }

    // 3) It fits somewhere between: short binary search (3 encodes).
    let lo = minimumQuality
    let hi = START_QUALITY
    let best = lowest
    for (let i = 0; i < 3; i++) {
        const q = (lo + hi) / 2
        const blob = await canvasToBlob(canvas, q)
        if (blob.size < TARGET_SIZE) {
            best = blob
            lo = q
        } else {
            hi = q
        }
    }
    return { blob: best, fits: true }
}

// ---------- Compression ----------

export async function compressImageFile(file) {
    if (!(file instanceof File) || file.size < MAX_IMAGE_SIZE) {
        return file
    }

    const image = await loadImage(file)

    // Start at a sensible size instead of 100%
    let scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height))

    try {
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            const atFloor = scale <= MIN_SCALE
            const canvas = drawOnCanvas(image, scale)
            const { blob, fits } = await encodeWithin(
                canvas,
                atFloor ? FALLBACK_MIN_QUALITY : MIN_QUALITY
            )

            if (fits) {
                const newName = file.name.replace(/\.[^/.]+$/, "") + ".jpg"
                return new File([blob], newName, { type: "image/jpeg" })
            }

            if (atFloor) break

            // File size ~ pixel count ~ scale², so jump directly to the right scale.
            // 0.95 gives a little safety margin.
            const estimated = scale * Math.sqrt(TARGET_SIZE / blob.size) * 0.95
            scale = Math.max(MIN_SCALE, Math.min(estimated, scale * 0.9))
        }
    } finally {
        image.close?.() // free ImageBitmap memory
    }

    throw new Error("Unable to compress image below 2 MB.")
}